// global BarcodeDetector

import { PropertyValueMap, css, html } from 'lit';
import { property, customElement, state } from 'lit/decorators.js';

import { router } from '../router.js';

import { map } from 'lit/directives/map.js';
import { classMap } from 'lit/directives/class-map.js';

import {
  durationToHuman,
  dateToInput,
  dateFormat,
  downloadContent,
  shareContent,
} from '../lib/utils.js';

import { ApplicationElement } from './application_element.js';

import '@shoelace-style/shoelace/dist/components/switch/switch.js';
import '@shoelace-style/shoelace/dist/components/spinner/spinner.js';
import '@shoelace-style/shoelace/dist/components/divider/divider.js';
import '@shoelace-style/shoelace/dist/components/dialog/dialog.js';
import '@shoelace-style/shoelace/dist/components/card/card.js';
import '@shoelace-style/shoelace/dist/components/icon-button/icon-button.js';
import '@shoelace-style/shoelace/dist/components/button/button.js';
import '@shoelace-style/shoelace/dist/components/drawer/drawer.js';
import '@shoelace-style/shoelace/dist/components/input/input.js';
import '@shoelace-style/shoelace/dist/components/alert/alert.js';
import '@shoelace-style/shoelace/dist/components/progress-bar/progress-bar.js';

import { styles } from '../styles/shared-styles.js';
import '../components/footer.js';
import '../components/menu.js';

import { Session } from '../models/session.js';
import '../components/timer.js';
import '../components/form-start-session.js';

@customElement('app-fast')
export class AppFast extends ApplicationElement {
  @state() settings = this.state?.settings;
  @state() config = this.state?.config;

  @state() sessions = this.state?.sessions || new Map();
  @state() lastSession = this.state?.lastSession;


  @property({ type: Object }) page: any;
  @property({ type: Object }) currentSession: any;

  @property({ type: Object }) fastingState = {};
  @property({ type: Number }) progress = 0;
  @property({ type: String }) fastingFor = '';

  static styles = [
    styles,
    css`
      main > * {
        margin-bottom: var(--sl-spacing-medium);
      }
      .session.failed .button {
        color: var(--sl-color-gray-500);
      }
      .session {
        .percent {
          width: 5ch;
          text-align: right;
        }
        .dates {
          text-align:right;
          width: 13ch;
        }
      }

      app-timer.big {
        display: block;
        font-size: var(--sl-font-size-2x-large);
        margin-bottom: var(--sl-spacing-2x-small);
      }

      sl-progress-bar {
        margin-bottom: var(--sl-spacing-small);

        &.over {
          --indicator-color: var(--sl-color-purple-600);
        }
      }

      .flux {
        font-size: var(--sl-font-size-x-large);
        text-align: center;

        i {
          margin-right: var(--sl-spacing-small);
        }
      }

      .main-button-wrapper {
        text-align: center;
        margin:  var(--sl-spacing-2x-large) 0;
      }


      ul, li{
        list-style: none;
        padding: 0;
        margin: 0;
      }

      ul {
        .button {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: var(--sl-spacing-x-small) var(--sl-spacing-medium);
          text-decoration: none;
          color: var(--sl-color-gray-800);
          background-color: var(--sl-color-gray-100);
          border-radius: var(--sl-border-radius-medium);
        }
        .button:hover {
          background-color: var(--sl-color-gray-200);
        }
        .button:active {
          background-color: var(--sl-color-gray-300);
        }

        li {
          margin-block: var(--sl-spacing-2x-small);
        }
      }

      li.eating {
        text-align: right;
        padding: var(--sl-spacing-x-small) var(--sl-spacing-medium);
        font-size: var(--sl-font-size-x-small);
        color: var(--sl-color-gray-500);
      }

      .eatingStatus::after {
        content: '';
        display: inline-block;
        --size: 5px;
        width: var(--size);
        height: var(--size);
        border-radius: 50%;
        vertical-align: middle;
        margin-right: -5px;
      }
      .eatingStatus.success::after {
        background-color: var(--sl-color-emerald-500);
      }

      .days-stats {
        text-align: center;
        font-size: var(--sl-font-size-x-small);
        color: var(--sl-color-gray-500);
      }

      .modal-main-action {
        text-align: center;
        margin-block: var(--sl-spacing-large);
        > * {
          width: 100%;
        }
      }

      .big-status {
        text-align: center;
        font-size: var(--sl-font-size-large);
        margin-block: var(--sl-spacing-small);
        line-height: 1.4;

        .status-icon {
          display:block;
          margin-bottom: var(--sl-spacing-x-small);
          font-size: 1.5em;
        }
        .status-text {
          display: block;
          font-size: var(--sl-font-size-medium);
          color: var(--sl-color-gray-600);

          &.completed {
            color: var(--sl-color-emerald-500);
          }

          &.overdone {
            color: var(--sl-color-purple-600);
          }

          &.almost {
            color: var(--sl-color-orange-600);
          }

          em {
            // color: var(--sl-color-gray-700);
          }
        }
      }

      .clean-link {
        text-decoration: none;
        display: block;
        color: var(--sl-color-neutral-0);

        &:active {
          background-color: var(--sl-color-gray-100);
          text-decoration: none;
        }
      }

    `,
  ];

  onStateChange({ state }) {
    // console.log('onStateChange', state);
    this.settings = state.settings;
    this.config = state.config;

    this.sessions = state.sessions || new Map();
    this.lastSession = state.lastSession;
    this.onTick();
  }

  connectedCallback(): void {
    super.connectedCallback();
    window.app = this;
    this.onTick();
    this.interval = setInterval(() => this.onTick(), 1000);
  }

  onTick() {
    this.fastingState = this.lastSession?.fastingState;
    this.progress = this.lastSession?.progress;
    this.fastingFor = this.lastSession?.fastingFor;
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    clearInterval(this.interval);
  }

  handleEvent(event: Event) {
    super.handleEvent(event);
  }

  get isAnyDialogOpened() {
    return this.shadowRoot.querySelector('sl-dialog[open]');
  }

  async toggleFasting() {
    if (this.lastSession?.isActive) {
      router.navigate(`/session/${this.lastSession.id}?mode=end`);
    } else {
      router.navigate(`/session/new?mode=start`);
    }
  }

  renderFastingWindow() {
    if (!this.lastSession?.isActive) return;

    const session = this.lastSession;

    return html`
      <div class="flex-items-distributed">
        <sl-card>
          <div slot="header" class="flex-ends">
            <span class="muted-text">Fasting</span>
            <span class="muted-text">Drink water!</span>
          </div>
          <div class="flex-ends">
            <app-timer
              class="big"
              .endsAt=${session.endsAtGoal}
            ></app-timer>

            <span class="muted-text">${Math.floor(this.progress)}%</span>

          </div>

          <sl-progress-bar class=${this.progressBarColor} value=${this.progress} style="--height: 6px;"></sl-progress-bar>

          <div class="muted-text">
            Fasting for ${this.fastingFor}
          </div>
        </sl-card>
      </div>

      <sl-alert variant=${this.fastingState.variant} open class="alert-closable">
        <i slot="icon" class=${this.fastingState.icon} style="font-size:1.5em;"></i>
        <strong>${this.fastingState.title}</strong><br>
        ${this.fastingState.desc}
      </sl-alert>

      <a href=${`/session/${session.id}?mode=start`} class="clean-link">
          <div class="flex-items-distributed">
            <div class="muted-text flex-ends" style="border-bottom:2px solid; padding: 8px 0">
              <div>
              Started<br>
              ${dateFormat(session.startedAt)}
              </div>
              <i class="bi-pencil"></i>
            </div>

            <div class="muted-text" style="border-bottom:2px solid; padding: 8px 0">
              End goal<br />${dateFormat(session.endsAtGoal)}
            </div>
          </div>
        </sl-card>
      </div>
      </a>
    `;
  }

  get progressBarColor() {
    return this.progress > 100 ? 'over' : '';
  }

  renderEatingWindow() {
    if (!this.lastSession?.isCompleted) return;

    const session = this.lastSession;

    return html`
      <div class="flex-items-distributed">
        <sl-card>
          <div slot="header" class="flex-ends">
            <span class="muted-text">Eating</span>
            <span class="muted-text">
              Nom nom nom
            </span>
          </div>

          <div><app-timer class="big" .endsAt=${session.nextFastBeginsAt}></app-timer></div>
          <div class="muted-text">Next fast: ${dateFormat(session.nextFastBeginsAt)}</div>
        </sl-card>
      </div>
    `;
  }

  renderStatus(percentComplete) {
    let icon = 'bi-x-circle';
    let color = '--sl-color-gray-400';

    if (percentComplete >= 103) {
      icon = 'bi-stars';
      color = '--sl-color-purple-600';
    } else if (percentComplete >= 100) {
      icon = 'bi-check-circle-fill';
      color = '--sl-color-emerald-600';
    } else if (percentComplete > 80) {
      icon = 'bi-clock-history';
      color = '--sl-color-orange-600';
    }

    return html`
      <span class="status-icon">
        <i class=${icon}
           style="color:var(${color}); font-size:1.5em;">
        </i>
    </span>`;
  }

  renderStatusText(percentComplete) {
    let text;
    let cssClass;

    if (percentComplete >= 101) {
      cssClass = 'overdone';
      text = html`
        <p>You absolutely crushed it!</p>
        <p>You are ${percentComplete - 100}% over your goal.</p>
      `;
    } else if (percentComplete >= 100) {
      cssClass = 'completed';
      text = 'Great job! You completed your fast by plan.';
    } else if (percentComplete > 80) {
      cssClass = 'almost';
      text = html`
        With ${percentComplete}% of your goal, you are almost there!
      `;
    } else {
      cssClass = 'underdone';
      text = html`
        <p>Some days are better than others.<br> You are at ${percentComplete}%</p>
        <p><em>Tomorrow will be better!</em></p>
      `;
    }

    return html`<div class="status-text ${cssClass}">${text}</div>`;
  }

  renderEating({ eatingDuration, eatingIsSuccessful }) {
    if (eatingDuration == null) return;

    return html`
      <li class="eating">
        <span class=${classMap({ eatingStatus: true, success: eatingIsSuccessful })}>
          Eating ${durationToHuman(eatingDuration, {size: 2})}
        </span>
      </li>
    `;
  }

  renderSession(session) {
    if (session.isActive) return;

    const { isActive } = session;

    return html`
      ${this.renderEating(session)}
      <li class=${classMap({ failed: session.isFailed, session: true })}>
        <a class="button" href="/session/${session.id}">
          ${this.renderStatus(session.percentComplete)}
          <span class="percent">${session.fluxHuman}</span>

          <span style="width: 60px">
            <span>${isActive ? 'In progress' : durationToHuman(session.duration, {size: 2})}</span>
          </span>

          <small class="dates">

            ${dateFormat(session.endedAt)}<br>
            ${dateFormat(session.startedAt)}
          </small>
        </a>
      </li>
    `;
  }

  renderSessions() {
    return html`
      <ul class="list3">
        ${map(this.sessions, ([_id, session]) => this.renderSession(session))}
      </ul>
    `;
  }

  get fasting() {
    return this.lastSession?.isActive;
  }

  // Drawer logic
  get drawer() {
    return this.shadowRoot?.querySelector('sl-drawer');
  }

  clearCurrentSession() {
    history.back();
  }

  protected async willUpdate(changedProperties) {
    super.willUpdate(changedProperties);

    if (changedProperties.has('page')) {
      const { id } = this.page.params;


      try{
        this.currentSession =
          id == 'new'
            ? new Session({
                startedAt: new Date(),
                fastingGoal: this.settings.fastingGoal,
                eatingGoal: this.settings.eatingGoal,
              })
            : await Session.find(Number(this.page.params.id));
      } catch (e) {
        return router.navigate("/");
      }

    }
    if (changedProperties.has('currentSession') && this.currentSession) {
      requestAnimationFrame(() => this.drawer?.show());
    }
  }

  renderDrawer() {
    const session = this.currentSession;

    if (!session) return;

    return html`
      <sl-drawer
        @sl-after-hide=${this.clearCurrentSession}
        placement="bottom"
        style="--size: auto;"
      >
        ${this.renderSessionForm(session)}
      </sl-drawer>
    `;
  }

  handleFormSubmit(event) {
    event.preventDefault();

    // console.log('handleFormSubmit', event);

    const form = event.target;
    const formData = new FormData(form);
    const data = Object.fromEntries(formData.entries());

    if(!form.checkValidity()) return;

    this.currentSession.update(data);
    this.drawer.hide();
  }

  onClickSessionDelete() {
    if (!confirm('Are you sure?')) return;

    this.currentSession.destroy();
    this.drawer.hide();
  }

  renderSessionForm(session) {
    const { mode } = this.page.query;
    if (!mode) return this.renderSessionFullForm(session);

    if (mode == 'end') {
      session.endedAt = new Date();
    }

    return mode == 'start'
      ? this.renderSessionStartForm(session)
      : this.renderSessionEndForm(session);
  }

  renderSessionStartForm(session) {
    return html`
      <div slot="label">Start fasting</div>

      <form-start-session
        .session=${session}
        .fastingGoalInMs=${this.settings.fastingGoalInMs}
        .handleSubmit=${(e) => this.handleFormSubmit(e)}
      ></form-start-session>
    `;
  }

  renderSessionEndForm(session) {
    const now = new Date();

    return html`
      <div slot="label">Finish fast</div>

      <article class="big-status">
        <div>
          ${this.renderStatus(session.progress)}
          ${this.renderStatusText(session.progress)}
        </div>

        <p>
          You fasted for: <br />
          <strong>${session.fastingFor}</strong>
        </p>


      </article>

      <sl-icon-button
        slot="header-actions"
        name="trash"
        label="Delete"
        @click=${this.onClickSessionDelete}
      ></sl-icon-button>

      <form @submit=${this.handleFormSubmit}>
        <sl-input
          required
          type="datetime-local"
          name="endedAt"
          label="End"
          value=${dateToInput(session.endedAt)}
          max=${dateToInput(now)}
          min=${dateToInput(session.startedAt)}
        ></sl-input>

        <br />

        <div class="modal-main-action">
          <sl-button type="submit" variant="success">Finish fasting session</sl-button>
        </div>
      </form>
    `;
  }

  renderSessionFullForm(session) {
    return html`
      <div slot="label">Edit #${session.id}</div>

      <p><strong>You fasted for:</strong> ${durationToHuman(session.duration, {size: 2})}</p>
      <p><strong>Percent:</strong> ${session.percentComplete}%</p>
      <p><strong>Plan:</strong> ${session.planHuman}</p>

      <sl-icon-button
        slot="header-actions"
        name="trash"
        label="Delete"
        @click=${this.onClickSessionDelete}
      ></sl-icon-button>



      <form @submit=${this.handleFormSubmit}>
        <sl-input
          required
          type="datetime-local"
          name="startedAt"
          label="Start"
          value=${dateToInput(session.startedAt)}
        ></sl-input>

        <br />

        <sl-input
          required
          ?disabled=${session.isActive}
          ?hidden=${session.isActive}
          type="datetime-local"
          name="endedAt"
          label="End"
          value=${dateToInput(session.endedAt)}
        ></sl-input>
        <br />

        <div class="modal-main-action">
          <sl-button type="submit" variant="primary">Update fasting session</sl-button>
        </div>
      </form>
    `;
  }

  async exportData() {
    const dump = await Session.dumpToJson();
    downloadContent(dump, `fasting_flux_export_${Date.now()}.json`, 'application/json');
    this.onBackupNudgeDialogClose();
    this.config.update({ lastBackupAt: new Date() });
  }

  async shareData() {
    const dump = await Session.dumpToJson();
    const success = await shareContent(dump, `fasting_flux_${Date.now()}.json.txt`, 'text/plain');
    this.onBackupNudgeDialogClose();
    if(success) this.config.update({ lastBackupAt: new Date() });
  }

  onBackupNudgeDialogClose() {
    this.config.backupNudgePresented = true;
  }

  renderBackupNudgeDialog() {
    if (!this.config.loaded) return;
    if (!this.config.shouldPresentBackupNudge) return;
    if (this.sessions.size <= 5) return;

    const msg = this.config.lastBackupAt ?
      `Your last backup was ${dateFormat(this.config.lastBackupAt)}.` :
      `You haven't backed up your data yet.`;

    return html`
      <sl-dialog
        open
        label="Backup your data"
        class="backup-nudge"
        @sl-hide=${this.onBackupNudgeDialogClose}
        >

        <p>${msg}</p>

        <p class="muted-text">
          <em>This dialog will remind you to backup your data every 7 days.</em>
        </p>

        <sl-button slot="footer" variant="primary" @click=${this.exportData}>Download</sl-button>
        <sl-button slot="footer" variant="primary" @click=${this.shareData}>Share</sl-button>
      </sl-dialog>
    `;
  }

  render() {
    const fastingButtonLabel = this.fasting ? 'End fasting' : 'Start fasting';
    const buttonVariant = this.fasting ? 'warning' : 'success';

    return html`
      <app-header logo title="Fasting"></app-header>

      <main>
        <article class="flex-ends">
          <div class="flux muted-text">
            <i class="bi-activity"></i>
            ${this.state?.totalFluxHuman}
          </div>
          <div>
            <span class="muted-text">Plan ${this.settings.planHuman}</span>
          </div>
        </article>


        ${this.renderFastingWindow()} ${this.renderEatingWindow()}

        <div class="main-button-wrapper">
          <sl-button @click=${this.toggleFasting} variant=${buttonVariant}
            >${fastingButtonLabel}</sl-button
          >
        </div>
        <div>${this.renderSessions()}</div>
      </main>

      ${this.renderDrawer()}
      ${this.renderBackupNudgeDialog()}

      <app-footer></app-footer>
    `;
  }
}

